import { GridRowClickEvent, GridCellProps } from "@progress/kendo-react-grid";
import KendoTableClientState from "@/components/shared/tables/KendoTableClientState";
import { SearchResult } from "@/models";
import { CHLD } from "@/models/chld.tsx";
import { formatDateOnly } from "@/utils/dates";
import { useChildEntities, useCoreData } from "@/queries";
import { useImperativeHandle, Ref } from "react";

type PreschoolSummary = {
  PreschoolCDSCode: string;
  PreschoolName: string;
};

type ChildSearchResultsProps = {
  searchResults: SearchResult<CHLD>[];
  searchedName?: string;
  searchedDOB?: string;
  onSelectChild: (child: CHLD) => void;
  onCreateNew: () => void;
  onBack: () => void;
  canCreateNew: boolean;
  preschools: PreschoolSummary[];
  ref?: Ref<ChildSearchResultsRef>;
};

export type ChildSearchResultsRef = {
  handleBack: () => void;
};

const CITY_STATE_COUNTRIES = new Set(["US", "CA", "MX"]);

const formatBirthLocation = (
  country: string | null | undefined,
  state: string | null | undefined,
  city: string | null | undefined,
  countryText: string
): string => {
  if (!country) return "";
  if (CITY_STATE_COUNTRIES.has(country.toUpperCase())) {
    const stateAbbr = state ? state.split("-").slice(1).join("-") : "";
    const parts = [city, stateAbbr].filter(Boolean);
    return parts.join(", ");
  }
  return countryText.toUpperCase();
};

const formatGender = (gender: string | null | undefined): string => {
  if (!gender) return "";

  switch (gender.toUpperCase()) {
    case "M":
      return "Male";
    case "F":
      return "Female";
    case "X":
      return "Nonbinary";
    default:
      return gender;
  }
};

export function ChildSearchResults({
  searchResults,
  searchedName,
  searchedDOB,
  onSelectChild,
  onCreateNew,
  onBack,
  canCreateNew,
  ref,
}: ChildSearchResultsProps) {
  const resultsCount = searchResults.length;

  const coreData = useCoreData();
  const leaList = useChildEntities("0-").data;
  const countryList = coreData.countryList;

  const getCountryText = (countryCode: string | null | undefined): string => {
    if (!countryCode) return "";
    const country = countryList.find((c) => c.Value === countryCode);
    return country ? country.Text : countryCode;
  };

  const handleRowClick = (event: GridRowClickEvent) => {
    const child: CHLD = event.dataItem.__raw as CHLD;
    onSelectChild(child);
  };

  const handleBackClick = () => {
    onBack();
  };

  useImperativeHandle(ref, () => ({
    handleBack: handleBackClick,
  }));

  const columns = [
    { field: "CSPPID", title: "CSPPID", width: "120px" },
    { field: "ChildLastName", title: "Last Name", width: "120px" },
    { field: "ChildFirstName", title: "First Name", width: "110px" },
    { field: "ChildDateOfBirth", title: "Birth Date", width: "110px" },
    { field: "BirthLocation", title: "Birth Location", width: "150px" },
    { field: "ChildGender", title: "Gender", width: "90px" },
    { field: "ReportingLEA", title: "LEA", width: "150px" },
    {
      field: "Score",
      title: "Match Score",
      width: "100px",
      cell: (props: GridCellProps) => {
        const { className, style, colSpan } = props;
        const score = props.dataItem.Score;
        return (
          <td className={className} style={style} colSpan={colSpan}>
            {typeof score === "number" ? score.toFixed(2) : ""}
          </td>
        );
      },
    },
  ];

  const tableData = searchResults.map((record) => ({
    CSPPID: record.matchRecord.CSPPID || "",
    ChildLastName: record.matchRecord.ChildLastName || "",
    ChildFirstName: record.matchRecord.ChildFirstName || "",
    ChildDateOfBirth: record.matchRecord.ChildDateOfBirth
      ? formatDateOnly(record.matchRecord.ChildDateOfBirth)
      : "",
    BirthLocation: formatBirthLocation(
      record.matchRecord.ChildCountryOfBirth,
      record.matchRecord.ChildStateOfBirth,
      record.matchRecord.ChildCityOfBirth,
      getCountryText(record.matchRecord.ChildCountryOfBirth)
    ),
    ChildGender: formatGender(record.matchRecord.ChildGender),
    ReportingLEA:
      leaList.find((x) => x.CDSCode == record.matchRecord.ReportingLEA)
        ?.LEAName || "",
    Score: record.score ?? null,
    __raw: record.matchRecord,
  }));

  return (
    <div style={{ padding: "0 24px 24px", background: "white" }}>
      <div style={{ textAlign: "center", marginBottom: "24px" }}>
        <h3
          style={{
            fontWeight: "600",
            marginBottom: "12px",
            fontSize: "16px",
            color: "#333",
          }}
        >
          {searchedName &&
          searchedName !== "Child" &&
          searchedDOB &&
          searchedDOB !== "N/A" ? (
            <>
              {searchedName}, Date of Birth {formatDateOnly(searchedDOB)} -{" "}
            </>
          ) : null}
          {resultsCount} Potential Match{resultsCount !== 1 ? "es" : ""} Found
        </h3>
        <p style={{ margin: 0, fontSize: "14px", color: "#666" }}>
          Select a record to pull in existing data.
        </p>
      </div>
      <div style={{ background: "white", borderRadius: "4px" }}>
        <div style={{ maxHeight: "350px", overflowY: "auto" }}>
          <KendoTableClientState
            data={tableData}
            columns={columns}
            onRowClick={handleRowClick}
            filterable={false}
            noItemsMessage="No matching children found"
            initialTake={searchResults.length}
          />
        </div>
      </div>

      {canCreateNew && (
        <>
          <div
            style={{
              position: "relative",
              margin: "20px 0",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <span
              style={{
                background: "white",
                padding: "0 20px",
                fontSize: "14px",
                fontWeight: "600",
                color: "#333",
              }}
            >
              Or, if the child is not already in the CAPSDAC system:
            </span>
          </div>
          <div style={{ textAlign: "center", marginTop: "32px" }}>
            <button
              type="button"
              onClick={onCreateNew}
              style={{
                padding: "12px 40px",
                background: "var(--color-p3)",
                border: "none",
                borderRadius: "4px",
                color: "white",
                fontSize: "15px",
                cursor: "pointer",
                fontWeight: "600",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              Continue with New Record
            </button>
          </div>
        </>
      )}

      {!canCreateNew && searchResults.length === 0 && (
        <div
          style={{
            textAlign: "center",
            marginTop: "32px",
            padding: "16px",
            background: "#f8f9fa",
            borderRadius: "4px",
            border: "1px solid #dee2e6",
          }}
        >
          <p style={{ margin: 0, fontSize: "14px", color: "#666" }}>
            No records found. To create a new record, please search again using
            First Name, Last Name, and Date of Birth.
          </p>
        </div>
      )}
    </div>
  );
}
